import { Card, CardContent, Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactNode } from 'react';

const StyledCard = styled(Card)<{ bgcolor: string }>(({ theme, bgcolor }) => ({
  background: bgcolor,
  borderRadius: 20,
  boxShadow: '0px 5px 14px rgba(0, 0, 0, 0.05)',
  border: 'none',
  '&:hover': {
    boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.08)'
  },
  transition: 'box-shadow 0.3s ease'
}));

const IconCircle = styled(Box)<{ gradient: string }>(({ gradient }) => ({
  width: 48,
  height: 48,
  borderRadius: '50%',
  background: gradient,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    color: '#FFFFFF',
    fontSize: 24
  }
}));

interface StatCardProps {
  title: string;
  value: string;
  change: string;
  timePeriod: string;
  icon: ReactNode;
  backgroundColor: string;
  iconGradient: string;
  isPositive?: boolean;
}

const StatCard = ({ 
  title, 
  value, 
  change, 
  timePeriod, 
  icon, 
  backgroundColor, 
  iconGradient,
  isPositive = true 
}: StatCardProps) => {
  return (
    <StyledCard bgcolor={backgroundColor}>
      <CardContent 
        sx={{ 
          p: '16px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          '&:last-child': {
            paddingBottom: '16px'
          }
        }}
      >
        {/* Top Section - Text and Icon */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          {/* Text */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: '24px',
                color: '#181818'
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 700,
                fontSize: '24px',
                lineHeight: '32px',
                color: '#151D48'
              }}
            >
              {value}
            </Typography>
          </Box>

          {/* Icon */}
          <IconCircle gradient={iconGradient}>
            {icon}
          </IconCircle>
        </Box>

        {/* Bottom Section - Change and Time (only show if provided) */}
        {(change || timePeriod) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {change && (
              <Typography
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 700,
                  fontSize: '14px',
                  lineHeight: '18px',
                  color: isPositive ? '#2DCE89' : '#F5365C'
                }}
              >
                {change}
              </Typography>
            )}
            {timePeriod && (
              <Typography
                sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '16px',
                  color: '#B62A67'
                }}
              >
                {timePeriod}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default StatCard;